import './styles.scss';

import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { SpotLabel } from '..';
import { AnimatedTransition } from '../../../../../../components/animated-transition';
import { PortfolioManagementContext } from '../../../../../../providers/portfolio-management';
import {
  orderRowsListType,
  RiskFiltersContext,
} from '../../../../../../providers/risk-management/risk-filters-provider';
import { riskTypes } from '../../../../../../providers/risk-management/risk-management-provider';
import { IAccountOption } from '../../../../../../providers/venue-provider';
import Utils from '../../../../../../utils/immersive_utils';
import { formatTokenPrice } from '../../../../../../utils/decimal-map';
import ReorderTableMenu from '../../components/reorder-table-menu';

import { SpotOverrideInput } from './spot-override-input';
import { getObscuredProps } from '../../../../../../hooks/use-session-tracker';

const { formatNumber } = Utils;

interface IProps {
  data: any[];
  header: any[];
  columnsFilter?: number[];
  ref?: any;
  riskType: riskTypes;
  accountOption: IAccountOption;
  currentTab: string;
  spotLabels: SpotLabel[];
}

const RISK_GROUPING_COLUMNS = [
  {
    id: 0,
    key: '-75',
    label: '-75%',
    items: [],
  },
  {
    id: 1,
    key: '-50',
    label: '-50%',
    items: ['-55%', '-60%', '-65%', '-70%'],
  },
  {
    id: 2,
    key: '-25',
    label: '-25%',
    items: ['-30%', '-35%', '-40%', '-45%'],
  },
  {
    id: 3,
    key: '-10',
    label: '-10%',
    items: ['-15%', '-20%'],
  },
  {
    id: 4,
    key: '-5',
    label: '-5%',
    items: ['-9%', '-8%', '-7%', '-6%'],
  },
  {
    id: 5,
    key: '-4',
    label: '-4%',
    items: [],
  },
  {
    id: 6,
    key: '-3',
    label: '-3%',
    items: [],
  },
  {
    id: 7,
    key: '-2',
    label: '-2%',
    items: [],
  },
  {
    id: 8,
    key: '-1',
    label: '-1%',
    items: [],
  },
  {
    id: 9,
    key: 'zero',
    label: '0%',
    items: [],
  },
  {
    id: 10,
    key: '+1',
    label: '1%',
    items: [],
  },
  {
    id: 11,
    key: '+2',
    label: '2%',
    items: [],
  },
  {
    id: 12,
    key: '+3',
    label: '3%',
    items: [],
  },
  {
    id: 13,
    key: '+4',
    label: '4%',
    items: [],
  },
  {
    id: 14,
    key: '+5',
    label: '5%',
    items: ['6%', '7%', '8%', '9%'],
  },
  {
    id: 15,
    key: '+10',
    label: '10%',
    items: ['15%', '20%'],
  },
  {
    id: 16,
    key: '+25',
    label: '25%',
    items: ['30%', '35%', '40%', '45%'],
  },
  {
    id: 17,
    key: '+50',
    label: '50%',
    items: ['55%', '60%', '65%', '70%'],
  },
  {
    id: 18,
    key: '+75',
    label: '75%',
    items: ['80%', '85%', '90%', '95%'],
  },
  {
    id: 19,
    key: '+100',
    label: '100%',
    items: ['105%', '110%', '115%', '120%'],
  },
  {
    id: 20,
    key: '+125',
    label: '125%',
    items: ['130%', '135%', '140%', '145%'],
  },
  {
    id: 21,
    key: '+150',
    label: '150%',
    items: [],
  },
];

const RiskTable = forwardRef((props: IProps, ref) => {
  const { data, header, columnsFilter, riskType, accountOption, currentTab, spotLabels } = props;
  const {
    filters: { orderRowsList, setOrderRowsList, setDefaultOrderRowsList, defaultOrderRowsList },
  } = useContext(RiskFiltersContext);

  const orderRowsListByType = orderRowsList[riskType];

  const { riskSettingsSelected } = useContext(PortfolioManagementContext);

  const {
    filters: {
      token,
      riskToken,
      expandableRows,
      setExpandableRows,
      expandableColums,
      setExpandableColums,
    },
  } = useContext(RiskFiltersContext);
  const [isExpanded, setIsExpanded] = useState(false);

  const isFreezeSpot = spotLabels.some((item) => item.labelKey === 'freezeSpot');

  const handleExpandable = (id: string) => {
    if (!expandableRows.includes(id)) {
      setExpandableRows((prev: any) => [...prev, id]);
    } else {
      setIsExpanded(false);
      setExpandableRows((prev: any) => prev.filter((item: any) => item !== id));
    }
  };

  const renderDataLeft = (block: any[]) => {
    const renderChildrenTableLeft = (arr: any[], level: number) => {
      return arr?.map((item, index) => {
        if (!!item.children && item.children.length) {
          return renderNodeItem(item, level, `${item.id}_${index}`);
        } else {
          return (
            <tr className={`level-${level}`} key={`${item.id}_${index}`}>
              <td style={{ width: '1000px' }} className={item.id === 'spot' ? 'spot' : ''}>
                <div
                  className="table-item"
                  style={{
                    paddingLeft: `${16 * level}px`,
                    width: '100%',
                  }}
                >
                  <span className="label first-label">{item.label}</span>
                  <span className="label">{item.position}</span>
                </div>
              </td>
            </tr>
          );
        }
      });
    };

    const getWidthNodeIdZero = (id: string) => {
      if (typeof id !== 'string') return;

      if (id === '0') return '75px';
      if (id.includes('0_0D') || id.includes('0D_0')) return '139px';
      return '';
    };

    const getClassNameForRow = (item: any, level: number) => {
      let className = `level-${level} `;
      if (item.id === '0' || item.id === '0_0D' || item.id === '0D_0') {
        className += 'highlight-row';
      }
      return className;
    };

    const getClassNameForCell = (itemIdEqualZero: boolean) => {
      return itemIdEqualZero ? 'td-id-zero' : '';
    };

    const getClassNameForNodeButton = (itemIdEqualZero: boolean) => {
      return itemIdEqualZero ? 'node-button-zero' : '';
    };

    const renderNodeItem = (item: any, level: number, key: string) => {
      const itemIdEqualZero =
        item.id === '0' || item.id.includes('0_0D') || item.id.includes('0D_0');

      return (
        <tr style={{ width: '100%' }} className={getClassNameForRow(item, level)} key={key}>
          <td
            style={{ paddingBottom: 0, width: '10000px' }}
            className={getClassNameForCell(itemIdEqualZero)}
          >
            <div className="cell-node">
              <div
                onClick={() => handleExpandable(item.id)}
                className={`node-button ${getClassNameForNodeButton(itemIdEqualZero)}`}
                style={{
                  paddingLeft: `${16 * level}px`,
                  width: getWidthNodeIdZero(item.id),
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`icon ${expandableRows.includes(item.id) && 'rotate'}`}
                />
                <span className={`label first-label ${level === 0 ? 'label-level-0' : ''}`}>
                  {item.label}
                </span>
                <span className={`label ${item.position === 'Position' ? 'label-position' : ''}`}>
                  {item.position}
                </span>
              </div>
              <div className={`${!itemIdEqualZero && 'items'} level-${level}`}>
                {expandableRows.includes(item.id) &&
                  item.children &&
                  renderChildrenTableLeft(item.children, level + 1)}
              </div>
            </div>
          </td>
        </tr>
      );
    };

    return renderChildrenTableLeft(block, 0);
  };

  const verifyRenderColumn = (index: number) => {
    if (!columnsFilter || columnsFilter.length === 0) return true;

    const columnValue = header[index];

    return columnsFilter[0] <= columnValue && columnsFilter[1] >= columnValue;
  };

  const renderDataContent = (block: any[]) => {
    const items: any[] = [];

    const dataLoop = (item: any, parent: string[], level: number) => {
      items.push({
        id: item.id,
        label: item.label,
        parent,
        values: item.values,
        level: level,
      });

      if (!!item.children && item.children.length) {
        item.children.forEach((child: any) => {
          dataLoop(child, [...parent, item.id], level + 1);
        });
      }
    };

    const formatValueForTable = (value: any, riskToken: string, rowId: string) => {
      const isNegative = parseFloat(value) < 0;
      const absoluteValue = Math.abs(value);

      const formattedValue =
        rowId === 'spot'
          ? formatTokenPrice(absoluteValue, token ?? '')
          : formatNumber(absoluteValue, riskToken === 'BASE' ? 4 : 2);

      return isNegative ? `(${formattedValue})` : formattedValue;
    };

    block.forEach((item) => {
      dataLoop(item, [], 0);
    });

    if (items.length === 0) return;

    return items?.map((item, index) => {
      let render = true;
      const itemIdEqualZero = item.id === '0' || item.id === '0_0D';

      item.parent.forEach((parent: any) => {
        if (!expandableRows.includes(parent)) {
          render = false;
        }
      });

      if (!render) return null;

      let lineClassname = '';
      lineClassname += `level-${item.level} `;

      if (itemIdEqualZero) {
        lineClassname += ` highlight-row`;
      }

      const renderCompleteColumns = (itemsLength: number, totalLength: number) => {
        const arr = Array.from(
          { length: totalLength - itemsLength },
          (_, i) => itemsLength + i + 1,
        );

        return arr.map((i) => {
          if (!verifyRenderColumn(i)) return null;

          const canShow =
            RISK_GROUPING_COLUMNS.find((h) => h.label === `${header[i]}%`)?.items !== undefined ||
            expandableColums.find((expandableColumn: any) =>
              expandableColumn.items.find((item: string) => item === `${header[i]}%`),
            );

          if (!canShow) {
            return null;
          }

          const isChildrenColumn = expandableColums.find((expandableColumn: any) =>
            expandableColumn.items.find((item: string) => item === `${header[i]}%`),
          );

          let className = `${isChildrenColumn ? 'children-column' : ''}`;

          if (header[i] === 0) {
            className += ' zero-column';
          }

          return (
            <td key={`complete_${i}`} className={className}>
              &nbsp;
            </td>
          );
        });
      };

      return (
        <tr className={lineClassname} key={`${item.id}__${index}`}>
          {item?.values?.map((val: any, i: number) => {
            if (!verifyRenderColumn(i)) return null;

            const headerValue = header[i];
            const headerLabel = `${headerValue}%`;

            const isExpandable = expandableColums.some((col: any) =>
              col.items.includes(headerLabel),
            );

            const canShow =
              RISK_GROUPING_COLUMNS.some((h) => h.label === headerLabel) || isExpandable;
            if (!canShow) return null;

            const value = parseFloat(val);
            const isSpot = item.id === 'spot';
            const isColumnZero = headerValue === 0;
            const isColumnZeroAndSpot = isSpot && isColumnZero;
            const freezeSpotActive = isFreezeSpot;

            const classNames = [
              isExpandable && 'children-column',
              isColumnZero && 'zero-column',
              isSpot && 'spot',
              isSpot && freezeSpotActive && 'freeze-spot',
              freezeSpotActive && isColumnZeroAndSpot && 'freeze-spot-column-zero',
              value < 0 && 'negative',
              !isNaN(value) && value >= 0 && 'row-values',
            ]
              .filter(Boolean)
              .join(' ');

            const cellContent =
              val === null || val === undefined
                ? '\u00A0'
                : isNaN(value)
                  ? val
                  : formatValueForTable(value, riskToken, item.id);

            if (isColumnZeroAndSpot && riskSettingsSelected.isFreezeSpotOverride) {
              return (
                <td key={`${header[i]}_${i}`} className={`spot-override ${classNames}`}>
                  <SpotOverrideInput
                    cellContent={cellContent}
                    riskType={riskType}
                    accountOption={accountOption}
                    {...getObscuredProps()}
                  />
                </td>
              );
            }

            return (
              <td key={`${header[i]}_${i}`} className={classNames} {...getObscuredProps()}>
                {cellContent}
              </td>
            );
          })}

          {item?.values?.length < header.length &&
            renderCompleteColumns(item.values.length, header.length)}
        </tr>
      );
    });
  };

  const consoleError = console.error;

  console.error = (err: any) => {
    if (err && (typeof err !== 'string' || !err.includes('cannot appear as a child'))) {
      consoleError(err);
      return null;
    }
  };

  const handlExpandable = useCallback(() => {
    if (!data || data.length === 0) return;

    const expandableArr: any[] = [];

    const loopingData = (obj: any) => {
      if (obj && obj.length > 0) {
        loopingData(obj.forEach(loopingData));
      }

      if (obj && obj.children && obj.children?.length > 0) {
        obj.children.forEach(loopingData);
        expandableArr.push(obj.id);
      }
    };

    data.forEach((block: any) => {
      loopingData(block);
    });

    setExpandableRows(expandableArr);
  }, [data]);

  const renderHeader = (column: any) => {
    const newHeaderColumn = RISK_GROUPING_COLUMNS.find((h) => h.label === column);

    if (newHeaderColumn?.items && newHeaderColumn.items.length > 0) {
      const IsShowing = !!expandableColums.find((col: any) => col.id === newHeaderColumn.id);

      const showLeft = newHeaderColumn?.items.length && parseInt(newHeaderColumn.key) < 0;
      const showRight = newHeaderColumn?.items.length && parseInt(newHeaderColumn.key) >= 0;

      return (
        <th
          key={column}
          style={{ width: '88px' }}
          className={`expandable-column ${column === '0%' && 'zero-column'}`}
          onClick={() => {
            setExpandableColums((prev: any) => {
              const prevItem = prev.find((prevItem: any) => prevItem.id === newHeaderColumn.id);

              if (prevItem) {
                return prev.filter((item: any) => prevItem.id !== item.id);
              }

              return [...prev, newHeaderColumn];
            });
          }}
        >
          {!!showLeft && (
            <FontAwesomeIcon
              icon={faChevronRight}
              className={`icon ${!IsShowing ? 'rotate' : ''}`}
            />
          )}
          {!!showLeft && <span> </span>}
          {column}
          {!!showRight && <span> </span>}
          {!!showRight && (
            <FontAwesomeIcon
              icon={faChevronRight}
              className={`icon ${IsShowing ? 'rotate' : ''}`}
            />
          )}
        </th>
      );
    } else {
      const canShow =
        RISK_GROUPING_COLUMNS.find((h) => h.label === column)?.items !== undefined ||
        expandableColums.find((expandableColumn: any) =>
          expandableColumn.items.find((item: string) => item === column),
        );

      if (canShow) {
        const parent = RISK_GROUPING_COLUMNS.find((h) => h.items.find((i: string) => i === column));

        return (
          <th
            key={column}
            style={{ width: '88px' }}
            className={`${parent ? 'expandable-column' : ''} ${column === '0%' && 'zero-column'}`}
            onClick={() => {
              setExpandableColums((prev: any) => {
                if (parent) {
                  return prev.filter((item: any) => item.id !== parent.id);
                }

                return prev;
              });
            }}
          >
            {column}
          </th>
        );
      }
    }
  };

  useImperativeHandle(ref, () => ({
    handleExpandableOrCollapse(expand: boolean) {
      if (expand) {
        handlExpandable();
        setIsExpanded(true);
      } else {
        setExpandableRows([]);
        setIsExpanded(false);
      }
    },
  }));

  const handleIfIsExpanded = useCallback(() => {
    if (isExpanded) {
      handlExpandable();
    }
  }, [handlExpandable, isExpanded]);

  const verifyDataType = (dataIds: { id: string; label: string }[]) => {
    if (dataIds.some((item) => item.label.includes('%'))) {
      return riskTypes.spotGrid;
    } else if (dataIds.some((item) => item.label.includes('-'))) {
      return riskTypes.spotTime;
    }

    return riskTypes.spotLadder;
  };

  const handleChangeTab = useCallback(() => {
    const dataWithBlocks = data.filter((block) => block && block.length > 0);

    const dataIds = dataWithBlocks.map((block) => ({
      id: block[0]?.id,
      label: block[0]?.label || block[0]?.id,
    }));

    if (currentTab !== riskType || verifyDataType(dataIds) !== riskType) return;

    setOrderRowsList((prev: orderRowsListType) => {
      const handleUpdateState = (prev: orderRowsListType) => {
        const currentArr = [...prev[riskType]];

        if (riskType === 'SPOT_LADDER') {
          const updatedArr = currentArr.filter((item) =>
            dataIds.some((dataId) => dataId.id === item.id),
          );

          const finalArr = [...updatedArr];
          dataIds.forEach((dataId) => {
            if (!finalArr.some((item) => item.id === dataId.id)) {
              finalArr.push(dataId);
            }
          });

          return {
            ...prev,
            [riskType]: finalArr,
          };
        }

        if (currentArr && currentArr.length > 0) {
          return prev;
        }

        return {
          ...prev,
          [riskType]: dataIds,
        };
      };
      const newState = handleUpdateState(prev);

      return newState;
    });

    setDefaultOrderRowsList((prev: any) => {
      return {
        ...prev,
        [riskType]: dataIds,
      };
    });
  }, [data, orderRowsList, riskType, currentTab, setDefaultOrderRowsList]);

  useEffect(() => {
    handleChangeTab();
  }, [data, currentTab]);

  useEffect(() => {
    handleIfIsExpanded();
  }, [handleIfIsExpanded, data]);

  const getBlockById = (id: string) => {
    const find = data.find((block: any) => block[0].id === id);

    if (find) {
      return find;
    }

    return null;
  };

  const handleChangeRowsOrder = (arr: any) => {
    setOrderRowsList((prev: any) => {
      return {
        ...prev,
        [riskType]: arr,
      };
    });
  };

  const renderItemWrapper = (fn: (...args: any[]) => any, id: string) => {
    const block = getBlockById(id);

    if (!block) return null;

    return fn(block);
  };

  const renderReorderMenu = useMemo(() => {
    return (
      <ReorderTableMenu
        items={orderRowsListByType}
        setItems={handleChangeRowsOrder}
        defaultOrderRowsList={defaultOrderRowsList[riskType]}
        riskType={riskType}
      />
    );
  }, [orderRowsList[riskType], riskType, orderRowsListByType, handleChangeRowsOrder]);

  return (
    <AnimatedTransition className="risk-table-container">
      <div className="risk-table-wrapper">
        <div className="risk-table">
          <div className="left-table">
            <table>
              <thead>
                <tr>
                  <th key="stock">Shock</th>
                </tr>
              </thead>
              {orderRowsListByType.map((item: any, index: number) => {
                return (
                  <tbody key={`left_block_${index}`}>
                    {renderItemWrapper(renderDataLeft, item.id)}
                  </tbody>
                );
              })}
            </table>
          </div>

          <div className="container-content-table">
            <table className="content-table">
              <thead>
                <tr>
                  {header
                    .filter((_v, index) => verifyRenderColumn(index))
                    .map((column) => renderHeader(`${column}%`))}
                </tr>
              </thead>
              {orderRowsListByType.map((item: any, index: number) => {
                return (
                  <tbody key={`content_block_${index}`}>
                    {renderItemWrapper(renderDataContent, item.id)}
                  </tbody>
                );
              })}
            </table>
          </div>
        </div>
        <div className="risk-reorder-container">{renderReorderMenu}</div>
      </div>
    </AnimatedTransition>
  );
});

RiskTable.displayName = 'RiskTable';

export default RiskTable;
